import { TabContext, TabList, TabPanel } from '@mui/lab';
import { Box, Tab } from '@mui/material';
import React from 'react';
import { useParams } from 'react-router-dom';
import { CerebroBreadcrumb, CerebroPageContainer, CerebroSecondaryButton } from '../../shared/components';
import { useDataRoomDiligenceRequestsQuery, useDataRoomDiligenceRequestUploadsQuery } from '../../shared/hooks';
import { CurrentUser, DataRoomDiligenceRequest, StagedDiligenceRequestUpload } from '../../shared/models';
import { AddQuestionForm } from './AddQuestionForm';
import { AddRequestDocumentForm } from './AddRequestDocumentForm';
import { DeleteDiligenceRequestDialog } from './DeleteDiligenceRequestDialog';
import { DiligenceRequestsList } from './DiligenceRequestsList';
import { DiligenceUploadList } from './DiligenceUploadList';
import { EditDiligenceRequestForm } from './EditDiligenceRequestForm';
import { UploadDiligenceRequestForm } from './UploadDiligenceRequestForm';
import { ViewDiligenceUploadDrawer } from './ViewDiligenceUploadDrawer';
import { AssociateDocumentsDrawer } from './AssociateDocumentsDrawer';

interface DiligenceRequestsPageProps {
	currentUser: CurrentUser;
}

export const DiligenceRequestsPage: React.FC<DiligenceRequestsPageProps> = ({ currentUser }) => {
	const { guid } = useParams<'guid'>();
	const requestsQuery = useDataRoomDiligenceRequestsQuery(guid);
	const uploadsQuery = useDataRoomDiligenceRequestUploadsQuery(guid, {
		enabled: currentUser.isEmployeeUser,
		retry: false,
	});

	const [tab, setTab] = React.useState('pending');
	const [addQuestionRequest, setAddQuestionRequest] = React.useState<boolean>(false);
	const [addDocumentRequest, setAddDocumentRequest] = React.useState<boolean>(false);
	const [uploadRequests, setUploadRequests] = React.useState<boolean>(false);
	const [editRequest, setEditRequest] = React.useState<DataRoomDiligenceRequest | null>(null);
	const [deleteRequest, setDeleteRequest] = React.useState<DataRoomDiligenceRequest | null>(null);
	const [viewUpload, setViewUpload] = React.useState<StagedDiligenceRequestUpload | null>(null);
	const [associateDocuments, setAssociateDocuments] = React.useState<DataRoomDiligenceRequest | null>(null);

	const closeDrawersAndDialogs = () => {
		setAddQuestionRequest(false);
		setAddDocumentRequest(false);
		setUploadRequests(false);
		setEditRequest(null);
		setDeleteRequest(null);
		setViewUpload(null);
		setAssociateDocuments(null);
	};

	const handleTabChange = (event: any, newValue: string) => {
		setTab(newValue);
	};

	return (
		<CerebroPageContainer maxWidth={'100%'}>
			<CerebroBreadcrumb>Diligence Requests</CerebroBreadcrumb>

			{currentUser.isEmployeeUser && (
				<Box sx={{ marginBottom: 2 }}>
					<CerebroSecondaryButton onClick={() => setAddQuestionRequest(true)}>
						Ask a Question
					</CerebroSecondaryButton>
					<CerebroSecondaryButton onClick={() => setAddDocumentRequest(true)} sx={{ marginLeft: 1 }}>
						Request Document(s)
					</CerebroSecondaryButton>
					<CerebroSecondaryButton sx={{ marginLeft: 1 }} onClick={() => setUploadRequests(true)}>
						Upload Requests
					</CerebroSecondaryButton>
				</Box>
			)}

			{uploadsQuery.isSuccess && uploadsQuery.data.length > 0 && (
				<DiligenceUploadList uploads={uploadsQuery.data} onView={setViewUpload} />
			)}

			<TabContext value={tab}>
				<Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
					<TabList onChange={handleTabChange}>
						<Tab label="Pending" value="pending" />
						<Tab label="Completed" value="completed" />
						<Tab label="Withdrawn" value="withdrawn" />
						<Tab label="All" value="all" />
					</TabList>
				</Box>
				<TabPanel value="pending">
					<DiligenceRequestsList
						dataRoomGuid={guid}
						isLoading={requestsQuery.isLoading}
						items={requestsQuery.data?.filter(w => !w.isCompleted && !w.isWithdrawn)}
						currentUser={currentUser}
						isTeamMember={false}
						tab="pending"
						onEdit={setEditRequest}
						onDelete={setDeleteRequest}
						onAssociate={setAssociateDocuments}
					/>
				</TabPanel>
				<TabPanel value="completed">
					<DiligenceRequestsList
						dataRoomGuid={guid}
						isLoading={requestsQuery.isLoading}
						items={requestsQuery.data?.filter(w => w.isCompleted)}
						currentUser={currentUser}
						isTeamMember={false}
						tab="completed"
						onEdit={setEditRequest}
						onDelete={setDeleteRequest}
						onAssociate={setAssociateDocuments}
					/>
				</TabPanel>
				<TabPanel value="withdrawn">
					<DiligenceRequestsList
						dataRoomGuid={guid}
						isLoading={requestsQuery.isLoading}
						items={requestsQuery.data?.filter(w => w.isWithdrawn)}
						currentUser={currentUser}
						isTeamMember={false}
						tab="completed"
						onEdit={setEditRequest}
						onDelete={setDeleteRequest}
						onAssociate={setAssociateDocuments}
					/>
				</TabPanel>
				<TabPanel value="all">
					<DiligenceRequestsList
						dataRoomGuid={guid}
						isLoading={requestsQuery.isLoading}
						items={requestsQuery.data}
						currentUser={currentUser}
						isTeamMember={false}
						tab="all"
						onEdit={setEditRequest}
						onDelete={setDeleteRequest}
						onAssociate={setAssociateDocuments}
					/>
				</TabPanel>
			</TabContext>

			<AddQuestionForm dataRoomGuid={guid} open={addQuestionRequest} close={closeDrawersAndDialogs} />
			<AddRequestDocumentForm dataRoomGuid={guid} open={addDocumentRequest} close={closeDrawersAndDialogs} />
			<EditDiligenceRequestForm dataRoomGuid={guid} request={editRequest} close={closeDrawersAndDialogs} />
			<UploadDiligenceRequestForm dataRoomGuid={guid} open={uploadRequests} close={closeDrawersAndDialogs} />
			<DeleteDiligenceRequestDialog
				dataRoomGuid={guid}
				request={deleteRequest}
				currentUser={currentUser}
				close={closeDrawersAndDialogs}
			/>
			<ViewDiligenceUploadDrawer dataRoomGuid={guid} upload={viewUpload} close={closeDrawersAndDialogs} />
			<AssociateDocumentsDrawer dataRoomGuid={guid} request={associateDocuments} close={closeDrawersAndDialogs} />
		</CerebroPageContainer>
	);
};
